import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import './index.css';
import App from './App';
import ErrorBoundary from './components/common/ErrorBoundary';
import { initAppInsights } from './utils/appInsights';

const isProduction = process.env.REACT_APP_ENVIRONMENT === 'production';

// --- Production telemetry: Azure Application Insights (PIPEDA, Canada Central) ---
// Errors/pageviews are PHI-scrubbed before send. No-op unless enabled via env.
initAppInsights();

// --- Non-production only: Sentry (local/staging convenience) ---
// Disabled in production for PIPEDA (replay/PII would leave the Canadian region).
// Even in non-prod we now disable PII and mask replay content, closing the gap
// flagged in TD-008.
const sentryDsn = process.env.REACT_APP_SENTRY_DSN;
if (sentryDsn && !isProduction) {
  Sentry.init({
    dsn: sentryDsn,
    environment: process.env.REACT_APP_ENVIRONMENT || 'development',
    release: `clinic-studios-frontend@${process.env.REACT_APP_VERSION || '1.0.0'}`,
    sendDefaultPii: false,
    tracesSampleRate: 1.0,
    enabled: true,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
}

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

root.render(
  <React.StrictMode>
    {/* App-owned boundary reports to App Insights (prod) + the logger (always),
        independent of Sentry so production error capture doesn't depend on it. */}
    <ErrorBoundary fallback={<p>An error occurred. Please refresh the page.</p>}>
      <App />
    </ErrorBoundary>
  </React.StrictMode>
);
